import { applyStreamEvent } from "@/handlers/messageHandlers";
import { parseSseChunk } from "@/handlers/streamParser";
import { APP_META } from "@/lib/utils";
import { log } from "@/lib/utils";

// @note Dynamic import for db functions to avoid bundling PGlite in Cloudflare Workers
// PGlite uses eval() which is not allowed in Workers. These functions are only used
// client-side for caching, so we lazy-load them only when needed in a browser context.
// We use a runtime check to avoid static analysis by bundlers.
const isBrowser = typeof window !== "undefined" && typeof indexedDB !== "undefined";

type DbFunctions = {
  loadTools: (serverId: string) => Promise<import("@/types").MCPTool[]>;
  loadResources: (serverId: string) => Promise<import("@/types").MCPResource[]>;
  saveTools: (serverId: string, tools: import("@/types").MCPTool[]) => Promise<void>;
  saveResources: (serverId: string, resources: import("@/types").MCPResource[]) => Promise<void>;
};

let dbModule: DbFunctions | null = null;

/**
 * Lazy-load db functions only in browser environment.
 * Uses runtime path construction to prevent bundlers from analyzing the import.
 */
const getDbModule = async (): Promise<DbFunctions | null> => {
  if (!isBrowser) return null;
  if (dbModule) return dbModule;
  try {
    // Use runtime string concatenation to prevent static analysis
    // This ensures the db module (with PGlite) is not bundled into Workers
    const modulePath = "./db";
    dbModule = await import(/* @vite-ignore */ modulePath) as DbFunctions;
    return dbModule;
  } catch (error) {
    log.warn("Failed to load db module (expected in server environment)", error);
    return null;
  }
};

import type {
  ChatMessage,
  MCPContextSnapshot,
  MCPResource,
  MCPServer,
  MCPStreamEvent,
  SendMessageRequest,
  MCPTool,
} from "@/types";

interface StreamParams {
  server: MCPServer;
  payload: SendMessageRequest;
  streamingMessageId: string;
  onEvent: (event: MCPStreamEvent) => void;
  onMessagesUpdate?: (messages: ChatMessage[]) => void;
  currentMessages?: ChatMessage[];
  signal?: AbortSignal;
}

interface MCPRequest {
  jsonrpc: "2.0";
  id: string | number;
  method: string;
  params?: Record<string, any>;
}
interface MCPResponse {
  jsonrpc: "2.0";
  id: string | number;
  result?: any;
  error?: {
    code: number;
    message: string;
    data?: unknown;
  };
}

interface InitializeRequest extends MCPRequest {
  method: "initialize";
  params: {
    protocolVersion: string;
    capabilities: Record<string, any>;
    clientInfo: {
      name: string;
      version: string;
    };
  };
}

interface InitializeResponse extends MCPResponse {
  result: {
    protocolVersion: string;
    capabilities: Record<string, any>;
    serverInfo: {
      name: string;
      version: string;
    };
    instructions?: string;
  };
}

interface ToolsListRequest extends MCPRequest {
  method: "tools/list";
  params?: {
    cursor?: string;
  };
}

interface ToolsListResponse extends MCPResponse {
  result: {
    tools: MCPTool[];
    nextCursor?: string;
  };
}

interface ToolsCallRequest extends MCPRequest {
  method: "tools/call";
  params: {
    name: string;
    arguments: Record<string, any>;
  };
}

export interface ToolsCallResponse extends MCPResponse {
  result: {
    content: Array<{
      type: string;
      text: string;
    }>;
  };
  error?: {
    code: number,
    message: string,
    data?: unknown
  }
}

interface MCPNotification {
  jsonrpc: "2.0";
  method: string;
  params?: Record<string, any>;
}

interface PingRequest extends MCPRequest {
  method: "ping";
  params: Record<string, never>;
}

interface PingResponse extends MCPResponse {
  result?: any;
  error?: {
    code: number;
    message: string;
  };
}

interface ResourcesListRequest extends MCPRequest {
  method: "resources/list";
  params?: {
    cursor?: string;
  };
}

interface ResourcesListResponse extends MCPResponse {
  result: {
    resources: MCPResource[];
    nextCursor?: string;
  };
}

/**
 * Minimal MCP client that talks directly to MCP servers using JSON-RPC over HTTP
 * and Server-Sent Events. No external MCP SDK is used.
 */
export class MCPClient {
  private buildHeaders(server: MCPServer, accept: string): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: accept,
      ...server.headers,
    };

    if (server.token) {
      headers.Authorization = `Bearer ${server.token}`;
    }

    return headers;
  }

  private async postJsonRpc<T>(server: MCPServer, body: any, accept = "application/json"): Promise<T> {
    log.info(`MCPClient: Request to ${server.name} - method: ${body.method}`);
    try {
      const headers = this.buildHeaders(server, accept);
      const res = await fetch(server.url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
        keepalive: true,
      });

      if (!res.ok) {
        throw new Error(`Request failed: ${res.status} ${res.statusText}`);
      }

      const data = (await res.json()) as any;
      if (data?.error) {
        throw new Error(data.error?.message ?? "Request returned an error");
      }
      return data as T;
    } catch (error) {
      log.error(error);
      throw error;
    }
  }

  async initialize(server: MCPServer): Promise<InitializeResponse["result"]> {
    const request: InitializeRequest = {
      jsonrpc: "2.0",
      id: Date.now(),
      method: "initialize",
      params: {
        protocolVersion: "2025-06-18",
        capabilities: {
          tools: true,
          experimental: true,
          roots: true,
        },
        clientInfo: {
          name: APP_META.name,
          version: APP_META.version,
        },
      },
    };

    const response = await this.postJsonRpc<InitializeResponse>(server, request);
    log.info(`MCPClient: Initialized with ${server.name} - server version: ${response.result.serverInfo.version}`);
    if (!response.result) {
      throw new Error("Initialize returned no result");
    }
    return response.result;
  }

  async listTools(server: MCPServer): Promise<MCPTool[]> {
    let cursor: string | undefined = undefined;
    const tools: MCPTool[] = [];

    do {
      const request: ToolsListRequest = {
        jsonrpc: "2.0",
        id: Date.now(),
        method: "tools/list",
        params: cursor ? { cursor } : undefined,
      };

      const response = await this.postJsonRpc<ToolsListResponse>(server, request);
      const batch = response.result?.tools ?? [];
      tools.push(
        ...batch.map(tool => ({
          ...tool,
          serverId: server.id,
          sourceServer: tool.sourceServer ?? server.name,
        })),
      );
      cursor = response.result?.nextCursor;
    } while (cursor);

    log.debug(`MCPClient: Fetched ${tools.length} tools from ${server.name}`);
    return tools;
  }

  async callTool(server: MCPServer, name: string, args: Record<string, any>): Promise<ToolsCallResponse["result"]> {
    if (server.transport === "mock") {
      return {
        content: [{ type: "text", text: `Mock tool ${name} called with ${JSON.stringify(args)}` }],
      };
    }

    const request: ToolsCallRequest = {
      jsonrpc: "2.0",
      id: Date.now(),
      method: "tools/call",
      params: {
        name,
        arguments: args,
      },
    };

    const response = await this.postJsonRpc<ToolsCallResponse>(server, request);
    if (response.error) {
      throw new Error(response.error.message || "Tool call failed");
    }
    log.debug(`MCPClient: Tool ${name} called on ${server.name}`);
    return response.result;
  }

  async listResources(server: MCPServer): Promise<MCPResource[]> {
    let cursor: string | undefined = undefined;
    const resources: MCPResource[] = [];

    do {
      const request: ResourcesListRequest = {
        jsonrpc: "2.0",
        id: Date.now(),
        method: "resources/list",
        params: cursor ? { cursor } : undefined,
      };

      const response = await this.postJsonRpc<ResourcesListResponse>(server, request);
      const batch = response.result?.resources ?? [];
      resources.push(
        ...batch.map(resource => ({
          ...resource,
          serverId: server.id,
        })),
      );
      cursor = response.result?.nextCursor;
    } while (cursor);

    log.debug(`MCPClient: Fetched ${resources.length} resources from ${server.name}`);
    return resources;
  }

  async getContextSnapshot(server: MCPServer): Promise<MCPContextSnapshot> {
    const now = new Date().toISOString();
    const snapshot: MCPContextSnapshot = {
      serverId: server.id,
      serverName: server.name,
      role: server.role,
      tools: [],
      resources: [],
      fetchedAt: now,
    };

    try {
      const init = await this.initialize(server);
      snapshot.instructions = init.instructions;
    } catch (error) {
      log.warn("Failed to initialize MCP server for context snapshot", error);
      snapshot.error = (error as any)?.message ?? "Initialization failed";
    }

    try {
      // Load tools from database cache instead of fetching from server
      // Only available in browser environment with IndexedDB
      const db = await getDbModule();
      if (db) {
        snapshot.tools = await db.loadTools(server.id);
      } else {
        // Server-side: fetch directly from MCP server
        snapshot.tools = await this.listTools(server);
      }
    } catch (error) {
      log.warn("Failed to load tools from cache for context snapshot", error);
      snapshot.error = snapshot.error ?? (error as any)?.message ?? "Tool cache load failed";
    }

    try {
      // Load resources from database cache instead of fetching from server
      // Only available in browser environment with IndexedDB
      const db = await getDbModule();
      if (db) {
        snapshot.resources = await db.loadResources(server.id);
      } else {
        // Server-side: fetch directly from MCP server
        snapshot.resources = await this.listResources(server);
      }
    } catch (error) {
      log.warn("Failed to load resources from cache for context snapshot", error);
      snapshot.error = snapshot.error ?? (error as any)?.message ?? "Resource cache load failed";
    }

    return snapshot;
  }

  /**
   * Refreshes context snapshot by fetching fresh data from the server
   * and saving it to the database cache. Use this method when you need
   * to force an update of tools and resources.
   */
  async refreshContextSnapshot(server: MCPServer, options?: { persist?: boolean }): Promise<MCPContextSnapshot> {
    const shouldPersist = options?.persist !== false;
    const now = new Date().toISOString();
    const snapshot: MCPContextSnapshot = {
      serverId: server.id,
      serverName: server.name,
      role: server.role,
      tools: [],
      resources: [],
      fetchedAt: now,
    };

    try {
      const init = await this.initialize(server);
      snapshot.instructions = init.instructions;
    } catch (error) {
      log.warn("Failed to initialize MCP server for refresh", error);
      snapshot.error = (error as any)?.message ?? "Initialization failed";
    }

    try {
      // Fetch fresh tools from server and save to database
      snapshot.tools = await this.listTools(server);
      if (shouldPersist && snapshot.tools?.length) {
        const db = await getDbModule();
        if (db) {
          await db.saveTools(server.id, snapshot.tools);
        }
      }
    } catch (error) {
      log.warn("Failed to refresh tools", error);
      snapshot.error = snapshot.error ?? (error as any)?.message ?? "Tool sync failed";
    }

    try {
      // Fetch fresh resources from server and save to database
      snapshot.resources = await this.listResources(server);
      if (shouldPersist && snapshot.resources?.length) {
        const db = await getDbModule();
        if (db) {
          await db.saveResources(server.id, snapshot.resources);
        }
      }
    } catch (error) {
      log.warn("Failed to refresh resources", error);
      snapshot.error = snapshot.error ?? (error as any)?.message ?? "Resource sync failed";
    }

    return snapshot;
  }

  /**
   * Fetches a live context snapshot directly from the MCP server without
   * persisting to any local cache. Intended for server-side usage where
   * IndexedDB/PGlite is unavailable.
   */
  async getLiveContextSnapshot(server: MCPServer): Promise<MCPContextSnapshot> {
    return this.refreshContextSnapshot(server, { persist: false });
  }

  async stream(params: StreamParams): Promise<() => void> {
    const { server } = params;
    if (server.transport === "mock") {
      return this.simulateStream(params);
    }

    if (server.transport === "http") {
      return this.callOnce(params);
    }

    return this.streamViaSse(params);
  }

  private async streamViaSse({
    server,
    payload,
    streamingMessageId,
    onEvent,
    onMessagesUpdate,
    currentMessages = [],
    signal,
  }: StreamParams): Promise<() => void> {
    const controller = new AbortController();
    const abortSignal = signal ?? controller.signal;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "text/event-stream, application/json",
      ...server.headers,
    };

    if (server.token) {
      headers.Authorization = `Bearer ${server.token}`;
    }

    const body = {
      jsonrpc: "2.0",
      id: Date.now(),
      method: "tools/call",
      params: {
        name: "messages/stream",
        arguments: {
          content: payload.message,
        },
      },
    };

    const response = await fetch(server.url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal: abortSignal,
    });

    if (!response.ok) {
      onEvent({ type: "error", data: `Request failed: ${response.status}` });
      return () => controller.abort();
    }

    onEvent({ type: "open", data: { status: response.status } });

    if (!response.body) {
      onEvent({ type: "error", data: "The server did not return a stream." });
      return () => controller.abort();
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    const processEvents = (events: MCPStreamEvent[]) => {
      for (const event of events) {
        onEvent(event);
        if (onMessagesUpdate) {
          currentMessages = applyStreamEvent(currentMessages, streamingMessageId, event);
          onMessagesUpdate(currentMessages);
        }
      }
    };

    const read = async (): Promise<void> => {
      const { done, value } = await reader.read();
      if (done) {
        onEvent({ type: "done" });
        return;
      }

      buffer += decoder.decode(value, { stream: true });
      const parsed = parseSseChunk(buffer);
      buffer = parsed.buffer;
      processEvents(parsed.events);
      await read();
    };

    read().catch(error => onEvent({ type: "error", data: String(error) }));

    return () => controller.abort();
  }

  private async callOnce({
    server,
    payload,
    onEvent,
  }: StreamParams): Promise<() => void> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...server.headers,
    };

    if (server.token) {
      headers.Authorization = `Bearer ${server.token}`;
    }

    const body = {
      jsonrpc: "2.0",
      id: Date.now(),
      method: "tools/call",
      params: {
        name: "messages/send",
        arguments: {
          content: payload.message,
        },
      },
    };

    try {
      const res = await fetch(server.url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      const data = await res.json();
      const text = typeof data === "string" ? data : JSON.stringify(data, null, 2);
      onEvent({ type: "message", data: text });
      onEvent({ type: "done" });
    } catch (error) {
      onEvent({ type: "error", data: String(error) });
    }

    return () => undefined;
  }

  private async simulateStream({
    payload,
    streamingMessageId,
    onEvent,
    onMessagesUpdate,
    currentMessages = [],
  }: StreamParams): Promise<() => void> {
    const script = [
      "Let me sync with the MCP server and read its context.",
      "Connected. Pulling recent resources and capabilities.",
      "Here is a quick response while the stream stays open:",
    ];

    let cancelled = false;

    const schedule = (lines: string[], delay = 500) => {
      lines.forEach((line, index) => {
        setTimeout(() => {
          if (cancelled) return;
          const event: MCPStreamEvent = index === lines.length - 1 ? { type: "done" } : { type: "delta", data: `${line} ` };
          if (event.type !== "done") {
            currentMessages = applyStreamEvent(currentMessages, streamingMessageId, event);
            onMessagesUpdate?.(currentMessages);
          }
          onEvent(event);
        }, delay * (index + 1));
      });
    };

    onEvent({ type: "open" });
    schedule(script, 550);
    setTimeout(() => {
      if (!cancelled) {
        onEvent({ type: "delta", data: payload.message ? `Echo: ${payload.message}` : "Ready for the next instruction." });
      }
    }, 2000);
    setTimeout(() => {
      if (!cancelled) onEvent({ type: "done" });
    }, 2600);

    return () => {
      cancelled = true;
    };
  }

  async ping(server: MCPServer): Promise<{ success: boolean, message?: string }> {
    const request: PingRequest = {
      jsonrpc: "2.0",
      id: Date.now(),
      method: "ping",
      params: {},
    };

    try {
      const res = await this.postJsonRpc<PingResponse>(server, request, "application/json");
      if (res.error) {
        return { success: false, message: res.error.message };
      }
      return { success: true };
    } catch (error) {
      return { success: false, message: (error as Error).message };
    }
  }
}
